import React, { useState } from 'react';
import { Card, Input, Button, Tag, Spin, Modal, message, Statistic, Drawer, List, Row, Col, Tooltip, DatePicker } from 'antd';
import { EyeOutlined, DeleteOutlined, PlusOutlined, ReloadOutlined, PrinterOutlined, EditOutlined, FileTextOutlined, CheckCircleOutlined, CalendarOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import dayjs, { Dayjs } from 'dayjs';
import styles from './StockOutPageMobile.module.css';
import { stockService } from '../../services/stockService';
import { medicineService } from '../../services/medicineService';
import StockOutPrintModal from '../../components/stock-out/StockOutPrintModal';
import StockOutModalMobile from '../../components/stock-out/StockOutModalMobile';
import { DatePickerModal } from '../../components/common/DatePickerModal';

interface StockOutItem {
  id: number;
  medicine_id: number;
  medicine_name: string;
  medicine_code: string;
  quantity: number;
  unit: string;
  unit_price: number;
  lot_id: number;
  lot_number: string;
  expiry_date: string;
  total_amount: number;
  notes: string;
  available_stock: number;
}

interface StockOutRecord {
  id: number;
  voucher_number: string;
  customer_name: string;
  customer_phone: string;
  customer_address: string;
  total_items: number;
  total_amount: number;
  status: 'pending' | 'completed' | 'cancelled';
  created_at: string;
  completed_at?: string;
  created_by_name: string;
  notes: string;
  items?: StockOutItem[];
}

const StockOutPageMobile: React.FC = () => {
  const [searchText, setSearchText] = useState('');
  const [dateFrom, setDateFrom] = useState<string>(dayjs().startOf('month').format('YYYY-MM-DD'));
  const [dateTo, setDateTo] = useState<string>(dayjs().endOf('month').format('YYYY-MM-DD'));
  const [modalVisible, setModalVisible] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [drawerRecord, setDrawerRecord] = useState<StockOutRecord | null>(null);
  const [printModalVisible, setPrintModalVisible] = useState(false);
  const [printRecord, setPrintRecord] = useState<StockOutRecord | null>(null);
  const [showFilters, setShowFilters] = useState(false);
  const [datePickerState, setDatePickerState] = useState<{
    isOpen: boolean;
    currentDate: Dayjs | null;
    field: 'dateFrom' | 'dateTo' | null;
  }>({
    isOpen: false,
    currentDate: null,
    field: null
  });
  const queryClient = useQueryClient();

  const { data: stockOutRecords = [], isLoading } = useQuery(
    ['stock-out', searchText, dateFrom, dateTo],
    () => stockService.getStockOutVouchers({
      search: searchText,
      start_date: dateFrom,
      end_date: dateTo,
      page: 1,
      pageSize: 1000
    }),
    { staleTime: 5 * 60 * 1000 }
  );

  // Lấy chi tiết phiếu xuất kho khi mở Drawer hoặc Modal in phiếu
  const { data: detailRecord, isLoading: isLoadingDetail } = useQuery(
    ['stock-out-detail', drawerRecord?.id],
    () => drawerRecord ? stockService.getStockOutVoucherDetail(drawerRecord.id) : null,
    { enabled: !!drawerRecord && drawerVisible }
  );
  const { data: printDetailRecord, isLoading: isLoadingPrint } = useQuery(
    ['stock-out-print', printRecord?.id],
    () => printRecord ? stockService.getStockOutVoucherDetail(printRecord.id) : null,
    { enabled: !!printRecord && printModalVisible }
  );

  const { data: medicines = [] } = useQuery(
    'medicines',
    () => medicineService.getMedicines(),
    { staleTime: 10 * 60 * 1000 }
  );

  const { data: editingRecord, isLoading: isLoadingEditing } = useQuery(
    ['stock-out-edit', editingId],
    () => editingId ? stockService.getStockOutVoucherDetail(editingId) : null,
    { enabled: !!editingId && modalVisible }
  );

  const deleteMutation = useMutation(
    (id: number) => stockService.deleteStockOutVoucher(id),
    {
      onSuccess: () => {
        message.success('Xóa phiếu xuất kho thành công!');
        queryClient.invalidateQueries('stock-out');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi xóa phiếu xuất kho');
      },
    }
  );

  const completeMutation = useMutation(
    (id: number) => stockService.completeStockOutVoucher(id),
    {
      onSuccess: () => {
        message.success('Hoàn thành xuất kho thành công!');
        queryClient.invalidateQueries('stock-out');
        queryClient.invalidateQueries('inventory');
      },
      onError: (error: any) => {
        message.error(error.response?.data?.message || 'Có lỗi xảy ra khi hoàn thành xuất kho');
      },
    }
  );

  const handleAdd = () => {
    setEditingId(null);
    setModalVisible(true);
  };

  const handleEdit = (record: StockOutRecord) => {
    setEditingId(record.id);
    setModalVisible(true);
  };

  const handleView = (record: StockOutRecord) => {
    setDrawerRecord(record);
    setDrawerVisible(true);
  };

  const handlePrint = (record: StockOutRecord) => {
    setPrintRecord(record);
    setPrintModalVisible(true);
  };

  const handleComplete = (id: number) => {
    Modal.confirm({
      title: 'Xác nhận hoàn thành xuất kho',
      content: 'Bạn có chắc chắn muốn hoàn thành phiếu xuất kho này? Hành động này sẽ cập nhật tồn kho.',
      okText: 'Hoàn thành',
      cancelText: 'Hủy',
      onOk: () => completeMutation.mutate(id),
    });
  };

  const handleDelete = (id: number) => {
    Modal.confirm({
      title: 'Xác nhận xóa phiếu xuất kho',
      content: 'Bạn có chắc chắn muốn xóa phiếu xuất kho này? Hành động này không thể hoàn tác.',
      okText: 'Xóa',
      cancelText: 'Hủy',
      okType: 'danger',
      onOk: () => deleteMutation.mutate(id),
    });
  };

  const openDatePicker = (field: 'dateFrom' | 'dateTo', currentDate?: string) => {
    setDatePickerState({
      isOpen: true,
      currentDate: currentDate ? dayjs(currentDate) : null,
      field
    });
  };

  const closeDatePicker = () => {
    setDatePickerState({
      isOpen: false,
      currentDate: null,
      field: null
    });
  };

  const handleDateSelect = (date: Dayjs) => {
    if (datePickerState.field) {
      if (datePickerState.field === 'dateFrom') {
        setDateFrom(date.format('YYYY-MM-DD'));
      } else {
        setDateTo(date.format('YYYY-MM-DD'));
      }
    }
    closeDatePicker();
  };

  const clearFilters = () => {
    setDateFrom(dayjs().startOf('month').format('YYYY-MM-DD'));
    setDateTo(dayjs().endOf('month').format('YYYY-MM-DD'));
  };

  return (
    <div className={styles['stockout-mobile-container']}>
      {/* ===== HEADER ===== */}
      <header className={styles['stockout-mobile-header']}>
        {/* Thanh tìm kiếm */}
        <div style={{ position: 'relative', marginBottom: 16 }}>
          <Input
            className={styles['stockout-mobile-search']}
            style={{ width: '100%', padding: '12px 40px 12px 16px', border: '1px solid #ced4da', borderRadius: 8, fontSize: 16, boxSizing: 'border-box' }}
            placeholder="Tìm kiếm phiếu xuất..."
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
          />
          <span style={{ position: 'absolute', right: 16, top: '50%', transform: 'translateY(-50%)', color: '#6c757d', fontSize: 18 }}>
            <ReloadOutlined />
          </span>
        </div>

        {/* Bộ lọc ngày */}
        <div style={{ marginBottom: 16 }}>
          {/* Bộ lọc */}
          <Card size="small" style={{ marginBottom: 8 }}>
            <Row gutter={8}>
              <Col span={12}>
                <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 500 }}>Từ ngày</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Input 
                    placeholder="Chọn ngày" 
                    value={dateFrom ? dayjs(dateFrom).format('DD/MM/YYYY') : ''}
                    readOnly
                    style={{ flex: 1, fontSize: 12 }}
                  />
                  <Button 
                    type="primary" 
                    icon={<CalendarOutlined />} 
                    onClick={() => openDatePicker('dateFrom', dateFrom)}
                    style={{ padding: '4px 8px', fontSize: 12 }}
                  />
                </div>
              </Col>
              <Col span={12}>
                <div style={{ marginBottom: 4, fontSize: 12, fontWeight: 500 }}>Đến ngày</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Input 
                    placeholder="Chọn ngày" 
                    value={dateTo ? dayjs(dateTo).format('DD/MM/YYYY') : ''}
                    readOnly
                    style={{ flex: 1, fontSize: 12 }}
                  />
                  <Button 
                    type="primary" 
                    icon={<CalendarOutlined />} 
                    style={{ padding: '4px 8px', fontSize: 12 }}
                    onClick={() => openDatePicker('dateTo', dateTo)}
                  />
                </div>
              </Col>
            </Row>
          </Card>
        </div>

      </header>
      <Spin spinning={isLoading}>
        <div className={styles['stockout-mobile-list']}>
          {stockOutRecords.map((item: StockOutRecord) => (
            <div
              key={item.id}
              className={styles['stockout-mobile-card']}
              style={{
                maxWidth: 420,
                width: '100%',
                background: '#fff',
                borderRadius: 16,
                boxShadow: '0 2px 12px rgba(0,0,0,0.06)',
                border: '1px solid #e5e7eb',
                margin: '0 auto 4px',
                overflow: 'hidden',
              }}
            >
              <div style={{ padding: 20 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 10 }}>
                  <div style={{ textAlign: 'left' }}>
                    <div style={{ fontWeight: 700, fontSize: 18, color: '#1890ff', textAlign: 'left' }}>{item.voucher_number}</div>
                    <div style={{ fontSize: 13, color: '#6b7280', marginTop: 4, textAlign: 'left' }}>Khách hàng: {item.customer_name}</div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <span style={{ fontSize: 14, color: '#6b7280', fontWeight: 500 }}>{dayjs(item.created_at).format('DD/MM/YYYY')}</span>
                  </div>
                </div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 8, fontSize: 15, textAlign: 'left' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
                    <span style={{ color: '#6b7280', textAlign: 'left' }}>Số lượng:</span>
                    <span style={{ fontWeight: 500, color: '#222', textAlign: 'left' }}>{item.total_items}</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
                    <span style={{ color: '#6b7280', textAlign: 'left' }}>Tổng tiền:</span>
                    <span style={{ fontWeight: 700, color: '#6366f1', textAlign: 'left' }}>{(item.total_amount || 0).toLocaleString('vi-VN')} VND</span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', textAlign: 'left' }}>
                    <span style={{ color: '#6b7280', textAlign: 'left' }}>Trạng thái:</span>
                    <span style={{ display: 'inline-flex', alignItems: 'center', padding: '4px 12px', borderRadius: 999, fontWeight: 600, fontSize: 13, background: item.status === 'pending' ? '#fef9c3' : item.status === 'completed' ? '#dcfce7' : '#fee2e2', color: item.status === 'pending' ? '#b45309' : item.status === 'completed' ? '#15803d' : '#b91c1c', textAlign: 'left' }}>
                      {item.status === 'completed' ? 'Hoàn thành' : item.status === 'pending' ? 'Chờ xử lý' : 'Đã hủy'}
                    </span>
                  </div>
                </div>
              </div>
              <div style={{ background: '#f9fafb', padding: '12px 24px', borderTop: '1px solid #e5e7eb' }}>
                <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => handleView(item)} className={styles['stockout-mobile-detail-btn']} style={{ padding: 8, borderRadius: '50%', color: '#1890ff', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                    <EyeOutlined />
                  </button>
                  <button onClick={() => handlePrint(item)} className={styles['stockout-mobile-detail-btn']} style={{ padding: 8, borderRadius: '50%', color: '#6c757d', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                    <PrinterOutlined />
                  </button>
                  {item.status === 'pending' && (
                    <>
                      <button onClick={e => { e.stopPropagation(); handleEdit(item); }} className={styles['stockout-mobile-detail-btn']} style={{ padding: 8, borderRadius: '50%', color: '#faad14', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                        <EditOutlined />
                      </button>
                      <button onClick={e => { e.stopPropagation(); handleComplete(item.id); }} className={styles['stockout-mobile-detail-btn']} style={{ padding: 8, borderRadius: '50%', color: '#52c41a', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                        <CheckCircleOutlined />
                      </button>
                      <button onClick={e => { e.stopPropagation(); handleDelete(item.id); }} className={styles['stockout-mobile-detail-btn']} style={{ padding: 8, borderRadius: '50%', color: 'red', background: 'none', border: 'none', fontSize: 20, transition: 'all 0.2s' }}>
                        <DeleteOutlined />
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
          {stockOutRecords.length === 0 && (
            <div className={styles['stockout-mobile-empty']}>Không có phiếu xuất kho nào phù hợp.</div>
          )}
        </div>
      </Spin>

      {/* Drawer xem chi tiết phiếu xuất kho */}
      <Drawer
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileTextOutlined />
            Chi tiết phiếu xuất kho
          </div>
        }
        placement="bottom"
        height="90vh"
        onClose={() => {
          setDrawerVisible(false);
          setDrawerRecord(null);
        }}
        open={drawerVisible}
        className={styles['stockout-mobile-drawer']}
      >
        {isLoadingDetail ? (
          <div style={{ textAlign: 'center', padding: '40px' }}>
            <div style={{ fontSize: 16, color: '#888' }}>Đang tải chi tiết...</div>
          </div>
        ) : detailRecord ? (
          <div>
            <Card size="small" style={{ marginBottom: 12 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <Statistic
                    title="Mã phiếu"
                    value={detailRecord.voucher?.voucher_number || detailRecord.voucher_number}
                    valueStyle={{ fontSize: '16px' }}
                  />
                </Col>
                <Col span={12}>
                  <Statistic
                    title="Tổng tiền"
                    value={detailRecord.voucher?.total_amount || detailRecord.total_amount}
                    suffix="VNĐ"
                    valueStyle={{ color: '#52c41a' }}
                    formatter={(value) => value?.toLocaleString('vi-VN')}
                  />
                </Col>
              </Row>
            </Card>
            <Card size="small" style={{ marginBottom: 12 }}>
              <Row gutter={8}>
                <Col span={12}>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Khách hàng:</strong>
                  </div>
                  <div>{detailRecord.voucher?.customer_name || detailRecord.customer_name}</div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: 8 }}>
                    <strong>Trạng thái:</strong>
                  </div>
                  <Tag color={detailRecord.voucher?.status === 'completed' ? 'green' : 'orange'}>
                    {detailRecord.voucher?.status === 'completed' ? 'Hoàn thành' : 'Chờ xử lý'}
                  </Tag>
                </Col>
              </Row>
            </Card>
            <Card size="small" style={{ marginBottom: 12 }}>
              <div style={{ marginBottom: 8 }}>
                <strong>Danh sách thuốc:</strong>
              </div>
              <List
                dataSource={detailRecord.items || []}
                renderItem={(item: any) => (
                  <List.Item>
                    <List.Item.Meta
                      title={item.medicine_name}
                      description={
                        <div>
                          <div>Số lượng: {item.quantity} {item.unit}</div>
                          <div>Đơn giá: {item.unit_price.toLocaleString('vi-VN')} VNĐ</div>
                          <div>Số lô: {item.lot_number}</div>
                          <div>HSD: {dayjs(item.expiry_date).format('DD/MM/YYYY')}</div>
                          {item.notes && (
                            <div>Ghi chú: {item.notes}</div>
                          )}
                        </div>
                      }
                    />
                  </List.Item>
                )}
              />
            </Card>
            {detailRecord.voucher?.notes && (
              <Card size="small">
                <div style={{ marginBottom: 8 }}>
                  <strong>Ghi chú:</strong>
                </div>
                <div>{detailRecord.voucher.notes}</div>
              </Card>
            )}
          </div>
        ) : (
          <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
            <FileTextOutlined style={{ fontSize: '48px', marginBottom: 16 }} />
            <div>Không tìm thấy thông tin chi tiết phiếu</div>
          </div>
        )}
      </Drawer>

      {/* Modal thêm/sửa phiếu xuất kho */}
      <StockOutModalMobile
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        editingRecord={editingId ? editingRecord : null}
        medicines={medicines}
        isLoadingDetail={isLoadingEditing}
      />

      {/* Modal in phiếu xuất kho */}
      <StockOutPrintModal
        visible={printModalVisible}
        onCancel={() => {
          setPrintModalVisible(false);
          setPrintRecord(null);
        }}
        data={printDetailRecord}
        isLoading={isLoadingPrint}
      />

      {/* Floating Add Button */}
      <button
        onClick={handleAdd}
        className={styles['stockout-mobile-add-btn']}
        title="Tạo phiếu xuất mới"
      >
        <PlusOutlined />
      </button>

      {/* ===== DATE PICKER MODAL ===== */}
      <DatePickerModal
        isOpen={datePickerState.isOpen}
        onClose={closeDatePicker}
        onSelect={handleDateSelect}
        selectedDate={datePickerState.currentDate || undefined}
        title={`Chọn ngày ${datePickerState.field === 'dateFrom' ? 'bắt đầu' : 'kết thúc'}`}
      />
    </div>
  );
};

export default StockOutPageMobile;
